import { useEffect, useState } from 'react'
import { Button, Select } from './ui'
import { contractsApi } from '../api/contracts'
import { fetchOperators, getProduct, type Operator, type Product } from '../api/catalog'
import { fetchSelectableProducts } from '../lib/productSelection'

const STATUSES_ALLOWING_CHANGE = ['Bozza', 'IntegrazioneRichiesta']

interface Props {
  contractId: number
  status: string
  productId?: number | undefined
  operatorId?: number | null | undefined
  departmentId?: number | null | undefined
  /** Called after the product was changed, so the page can reload the contract. */
  onChanged: () => void
}

/**
 * F-055 (TASK-0309): the "Prodotto" row of the practice detail - product name and the operator the
 * server derived from it, plus "Cambia" while the practice is in Bozza / IntegrazioneRichiesta.
 * Self-contained so the page only needs to render it. No amounts are shown or fetched.
 */
export function ContractProductRow({ contractId, status, productId, operatorId, departmentId, onChanged }: Props) {
  const [productName, setProductName] = useState<string | null>(null)
  const [operators, setOperators] = useState<Operator[]>([])
  const [editing, setEditing] = useState(false)
  const [options, setOptions] = useState<Product[]>([])
  const [choice, setChoice] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canChange = STATUSES_ALLOWING_CHANGE.includes(status)

  useEffect(() => {
    setProductName(null)
    if (productId == null) return
    let cancelled = false
    getProduct(productId)
      .then(r => { if (!cancelled) setProductName(r.product.name) })
      .catch(() => { if (!cancelled) setProductName(null) })
    return () => { cancelled = true }
  }, [productId])

  useEffect(() => {
    if (operatorId == null) return
    let cancelled = false
    fetchOperators()
      .then(r => { if (!cancelled) setOperators(r.items) })
      .catch(() => { if (!cancelled) setOperators([]) })
    return () => { cancelled = true }
  }, [operatorId])

  function startEditing(): void {
    setError(null)
    setChoice('')
    setEditing(true)
    fetchSelectableProducts(departmentId ?? undefined)
      .then(setOptions)
      .catch(() => setOptions([]))
  }

  async function save(): Promise<void> {
    if (!choice) return
    setSaving(true)
    setError(null)
    try {
      await contractsApi.assignProduct(contractId, Number(choice))
      setEditing(false)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio del prodotto')
    } finally {
      setSaving(false)
    }
  }

  const operatorName =
    operatorId != null ? (operators.find(o => o.id === operatorId)?.name ?? `#${operatorId}`) : null

  return (
    <>
        {productId != null ? (productName ?? `#${productId}`) : '—'}
        {operatorName && <span className="text-muted md-typescale-body-small ms-2">Operatore: {operatorName}</span>}
        {canChange && !editing && (
          <Button type="button" variant="text" size="sm" className="ms-2" onClick={startEditing}>
            Cambia
          </Button>
        )}
        {editing && (
          <div className="mt-2">
            <Select
              label="Nuovo prodotto"
              labelHidden
              value={choice}
              onChange={e => setChoice(e.target.value)}
            >
              <option value="">Seleziona un prodotto…</option>
              {options.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            {error && <div className="text-danger md-typescale-body-small mt-1">{error}</div>}
            <div className="mt-2 d-flex gap-2">
              <Button
                type="button"
                variant="tonal"
                size="sm"
                disabled={saving || !choice}
                onClick={() => void save()}
              >
                {saving ? 'Salvataggio…' : 'Salva'}
              </Button>
              <Button
                type="button"
                variant="text"
                size="sm"
                disabled={saving}
                onClick={() => setEditing(false)}
              >
                Annulla
              </Button>
            </div>
          </div>
        )}
    </>
  )
}
